import React from 'react';
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Chip,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Home as HomeIcon,
  ChevronRight as ChevronRightIcon,
  ArrowBack as ArrowBackIcon,
  People as PeopleIcon
} from '@mui/icons-material';

interface NavigationPathItem {
  userId: number;
  username: string;
  fullName: string;
  level: number;
}

interface CommissionBreadcrumbProps {
  navigationPath: NavigationPathItem[];
  onBreadcrumbClick: (userId: number) => void;
  currentDownlineCount: number;
}

const CommissionBreadcrumb: React.FC<CommissionBreadcrumbProps> = ({
  navigationPath,
  onBreadcrumbClick,
  currentDownlineCount
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Limit breadcrumbs on mobile
  const maxBreadcrumbs = isMobile ? 3 : 5;
  const displayPath = navigationPath.length > maxBreadcrumbs
    ? [
        navigationPath[0],
        { userId: -1, username: '...', fullName: '...', level: 0 } as NavigationPathItem,
        ...navigationPath.slice(-2)
      ]
    : navigationPath;

  const currentLevel = navigationPath[navigationPath.length - 1]?.level || 1;

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        borderRadius: 0,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* Back Button */}
        {navigationPath.length > 1 && (
          <IconButton
            size="small"
            onClick={() => {
              const previousUser = navigationPath[navigationPath.length - 2];
              onBreadcrumbClick(previousUser.userId);
            }}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}

        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<ChevronRightIcon fontSize="small" />}
          sx={{ flex: 1 }}
        >
          {displayPath.map((item, index) => {
            const isLast = index === displayPath.length - 1;
            const isEllipsis = item.userId === -1;
            const isRoot = index === 0;

            if (isEllipsis) {
              return (
                <Typography key="ellipsis" variant="body2" color="text.secondary">
                  ...
                </Typography>
              );
            }

            if (isLast) {
              return (
                <Box key={item.userId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isRoot && <HomeIcon sx={{ fontSize: 18 }} />}
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    {isMobile ? item.username : item.fullName}
                  </Typography>
                  <Chip
                    label={`L${item.level}`}
                    size="small"
                    color="primary"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              );
            }

            return (
              <Link
                key={item.userId}
                component="button"
                variant="body2"
                onClick={() => onBreadcrumbClick(item.userId)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'underline'
                  }
                }}
              >
                {isRoot && <HomeIcon sx={{ fontSize: 16 }} />}
                {isMobile ? item.username : item.fullName}
              </Link>
            );
          })}
        </Breadcrumbs>

        {/* Stats */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            <strong>{currentDownlineCount}</strong> {currentDownlineCount === 1 ? 'Downline' : 'Downlines'}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default CommissionBreadcrumb;

